import GameObject from '../../../GameObject/GameObject';
import TransformComponent from '../../../Component/Transform';
import AnimationClip from '../AnimationClip';
import Point from '../../../Utility/Point';
import BaseCard from '../../../Prototype/Level/Card/BaseCard';
import BoomCard from '../../../Prototype/Level/Card/BoomCard';
import { Easing } from '@tweenjs/tween.js';
import Renderable from '../../../Component/Renderable';
import { Random } from '../../../Utility/Random';
import { delay } from '../../../Utility/Task/TaskQueue';
import {
    GetAnimationClip,
    PlayAnimation,
    StartAnimation,
} from '../AnimationHelper';
import CardSystem from '../../Card/CardSystem';
import { showCardScoreEffectAt } from '../CardAnimationHelper';
import { UIEvent } from '../../UI/BaseUISystem';
import Container from '../../../Prototype/Container';

interface ICardExplosionMeta {
    system: CardSystem;
    sourceCard: BoomCard;
    targetCards: BaseCard[];
}

export class CardExplosion extends AnimationClip {
    public *execute(
        animator: GameObject,
        meta: ICardExplosionMeta
    ): IterableIterator<number> {
        const explosion = GameObject.Instantiate(Container);
        meta.sourceCard.getComponent(Renderable).visible = false;
        meta.sourceCard.addChild(explosion);
        animator.addChild(meta.sourceCard);
        animator.addChild(explosion);

        StartAnimation(explosion, 'BoomExplosionEffect');

        const cardsPositionAndScore: any[] = [
            {
                position: meta.sourceCard
                    .getComponent(TransformComponent)
                    .position.clone() as Point,
                score: meta.sourceCard.score,
            },
        ];

        for (const targetCard of meta.targetCards) {
            targetCard.getComponent(Renderable).visible = true;
            animator.addChild(targetCard);

            cardsPositionAndScore.push({
                position: targetCard
                    .getComponent(TransformComponent)
                    .position.clone() as Point,
                score: targetCard.score,
            });
        }

        const overlay = animator.getChildByName('overlay');
        StartAnimation(overlay, 'OverlayFadeIn');

        const durationIn = 450;
        const durationOut = 800;

        yield* this.cardsTweenIn(meta, durationIn, animator);

        yield* delay(durationIn);

        yield* this.cardsTweenOut(meta, durationOut);

        cardsPositionAndScore.forEach(card => {
            showCardScoreEffectAt(
                meta.system.scene,
                animator,
                card.score,
                card.position
            );
        });

        this.backgroundShake();

        yield* delay(durationOut);

        yield* PlayAnimation(GetAnimationClip('OverlayFadeOut', overlay));

        explosion.destroy();
        meta.system.scene.emit(UIEvent.RESTORE_INTERACTIVITY, true);
    }

    private backgroundShake() {
        const background = GameObject.findByName('backgroundSprite')[0];
        if (background) {
            StartAnimation(background, 'LevelScreenShake', {
                amplitude: 15,
                frequency: 25,
                damping: 0.9,
            });
        }
    }

    private *cardsTweenIn(
        meta: ICardExplosionMeta,
        durationIn: number,
        animator: GameObject
    ): IterableIterator<number> {
        const sourceCardTransform = meta.sourceCard.getComponent(
            TransformComponent
        );

        for (let card of meta.targetCards) {
            // Add as child to animator to bring in front of overlay.
            animator.addChild(card);

            const transform = card.getComponent(TransformComponent);
            const translateTween = this.getTween(transform.position);
            const rotationTween = this.getTween(transform);

            const rotDirection = Random.int(0, 1) * 2 - 1;
            const dx = transform.x - sourceCardTransform.x;
            const dy = transform.y - sourceCardTransform.y;
            const motionVector = new Point(dx, dy);
            const normalVector = motionVector.clone();
            Point.normalize(normalVector);

            // Tween in.
            translateTween
                .to(
                    {
                        x: sourceCardTransform.x + motionVector.x * 0.25,
                        y: sourceCardTransform.y + motionVector.y * 0.25,
                    },
                    durationIn
                )
                .easing(Easing.Quadratic.InOut)
                .start(this.elapsedTime);

            rotationTween
                .to({ rotation: Math.PI * -0.5 * rotDirection }, durationIn)
                .easing(Easing.Linear.None)
                .start(this.elapsedTime);
        }
    }

    private *cardsTweenOut(
        meta: ICardExplosionMeta,
        durationOut: number
    ): IterableIterator<number> {
        const sourceCardTransform = meta.sourceCard.getComponent(
            TransformComponent
        );

        for (let card of meta.targetCards) {
            const renderable = card.getComponent(Renderable);
            const transform = card.getComponent(TransformComponent);
            const translateTween = this.getTween(transform.position);
            const rotationTween = this.getTween(transform);

            const rotDirection = Random.int(0, 1) * 2 - 1;
            const dx = transform.x - sourceCardTransform.x;
            const dy = transform.y - sourceCardTransform.y;
            const motionVector = new Point(dx, dy);
            const normalVector = motionVector.clone();
            Point.normalize(normalVector);

            // Explode out.
            translateTween
                .to(
                    {
                        x: sourceCardTransform.x + normalVector.x * 1200,
                        y: sourceCardTransform.y + normalVector.y * 1000,
                    },
                    durationOut
                )
                .easing(Easing.Quadratic.Out)
                .start(this.elapsedTime);

            rotationTween
                .to({ rotation: Math.PI * 6 * -rotDirection }, durationOut)
                .easing(Easing.Quadratic.Out)
                .start(this.elapsedTime)
                .onComplete(() => (renderable.visible = false));
        }
    }
}
